/**
 * signatureGeometry — the pure half of InputSignature.vue.
 *
 * Everything here is a function of its arguments: no canvas, no DOM, no
 * pointer events. That is what makes the interesting decisions testable —
 * "is this a signature or an accidental tap" is a rule about captured points,
 * and it should not need a browser to prove.
 *
 * The component owns the canvas and the pointer plumbing; this module owns
 * the geometry and the verdict.
 */

export interface SignaturePoint {
  x: number;
  y: number;
  /** Milliseconds since capture began. Used only for the duration metric. */
  t: number;
}

export type SignatureStroke = SignaturePoint[];

export interface SignatureMetrics {
  /** How many separate pen-downs. */
  strokes: number;
  /** Total captured points across every stroke. */
  points: number;
  /** Time from the first point to the last, in milliseconds. */
  durationMs: number;
  /** Width of the drawn area, in canvas units. */
  width: number;
  /** Height of the drawn area, in canvas units. */
  height: number;
  /** Summed straight-line length of every stroke, in canvas units. */
  length: number;
}

export const EMPTY_METRICS: SignatureMetrics = {
  strokes: 0,
  points: 0,
  durationMs: 0,
  width: 0,
  height: 0,
  length: 0,
};

/**
 * A tap, a slip of the finger while scrolling, or a single dot is not a
 * signature. Two independent thresholds, because either alone is easy to hit
 * by accident: a coarse drag covers ground with almost no samples, and a
 * finger resting on the glass emits samples without going anywhere.
 *
 * The second threshold is the BOUNDING BOX, not the path length. Path length
 * was the first attempt and it fails: a finger resting on a touchscreen jitters
 * a pixel or two per sample, and forty of those sum past any sane length bar
 * while the pen has covered a 1x1 box. Extent cannot be accumulated by
 * standing still.
 */
export const MIN_POINTS = 8;
export const MIN_EXTENT = 40;

export function metrics(strokes: SignatureStroke[]): SignatureMetrics {
  const points = strokes.flat();
  if (points.length === 0) {
    return { ...EMPTY_METRICS };
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let firstT = Infinity;
  let lastT = -Infinity;

  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
    if (p.t < firstT) firstT = p.t;
    if (p.t > lastT) lastT = p.t;
  }

  let length = 0;
  for (const stroke of strokes) {
    for (let i = 1; i < stroke.length; i++) {
      const dx = stroke[i].x - stroke[i - 1].x;
      const dy = stroke[i].y - stroke[i - 1].y;
      length += Math.sqrt(dx * dx + dy * dy);
    }
  }

  return {
    strokes: strokes.filter((s) => s.length > 0).length,
    points: points.length,
    durationMs: Math.max(0, Math.round(lastT - firstT)),
    width: maxX - minX,
    height: maxY - minY,
    length: Math.round(length),
  };
}

/**
 * Whether what was drawn counts as a signature.
 *
 * Deliberately NOT a judgement about whether it looks like handwriting, or
 * like the signer's name. That is not something software can decide, and
 * pretending otherwise would reject legitimate signatures while stopping no
 * determined person. The only claim made here is that something was drawn on
 * purpose.
 */
export function isMeaningful(m: SignatureMetrics): boolean {
  const extent = Math.sqrt(m.width * m.width + m.height * m.height);

  return m.points >= MIN_POINTS && extent >= MIN_EXTENT;
}

/**
 * Resample a stroke so redrawing after a resize does not need the old bitmap.
 * Scaling the pixels blurs; replaying the points is exact.
 */
export function scaleStrokes(
  strokes: SignatureStroke[],
  factorX: number,
  factorY: number,
): SignatureStroke[] {
  return strokes.map((stroke) =>
    stroke.map((p) => ({ x: p.x * factorX, y: p.y * factorY, t: p.t })),
  );
}

/**
 * Midpoint smoothing: draw each segment as a quadratic curve through the
 * midpoints of consecutive samples. Straight line-to-line joins look like a
 * seismograph on a touchscreen, where samples arrive far apart.
 *
 * Returns the sequence of drawing ops so the component can replay them onto
 * any context, and a test can assert the shape without a canvas.
 */
export type SignatureOp =
  | { op: 'move'; x: number; y: number }
  | { op: 'line'; x: number; y: number }
  | { op: 'quad'; cx: number; cy: number; x: number; y: number };

export function strokeToOps(stroke: SignatureStroke): SignatureOp[] {
  if (stroke.length === 0) {
    return [];
  }

  if (stroke.length === 1) {
    // A single sample still has to leave a mark, otherwise a deliberate dot
    // on an "i" disappears.
    return [
      { op: 'move', x: stroke[0].x, y: stroke[0].y },
      { op: 'line', x: stroke[0].x, y: stroke[0].y },
    ];
  }

  const ops: SignatureOp[] = [{ op: 'move', x: stroke[0].x, y: stroke[0].y }];

  for (let i = 1; i < stroke.length - 1; i++) {
    const midX = (stroke[i].x + stroke[i + 1].x) / 2;
    const midY = (stroke[i].y + stroke[i + 1].y) / 2;
    ops.push({ op: 'quad', cx: stroke[i].x, cy: stroke[i].y, x: midX, y: midY });
  }

  const last = stroke[stroke.length - 1];
  ops.push({ op: 'line', x: last.x, y: last.y });

  return ops;
}
